import type { FundamentalDto } from '@/api/recognition';
import type { BulkEditableRow } from './types';

export interface ParsedCsvRow {
  values: Record<string, string>;
  /** Línea real dentro del archivo original (1-based, la cabecera cuenta como línea 1). */
  lineNumber: number;
}

/** Parser CSV mínimo con soporte de campos entre comillas y comas internas. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      out.push(current); current = '';
    } else {
      current += char;
    }
  }
  out.push(current);
  return out.map((value) => value.trim());
}

/** Convierte el texto crudo del archivo en filas con nombre de columna → valor. */
export function parseCsvText(text: string): ParsedCsvRow[] {
  const allLines = text.replace(/\r\n/g, '\n').split('\n');
  if (allLines.every((line) => line.trim().length === 0)) return [];

  const headers = splitCsvLine(allLines[0]).map((header) => header.replace(/^"|"$/g, ''));
  const rows: ParsedCsvRow[] = [];
  for (let i = 1; i < allLines.length; i++) {
    const line = allLines[i];
    if (line.trim().length === 0) continue;
    const columns = splitCsvLine(line);
    const values: Record<string, string> = {};
    headers.forEach((header, col) => { values[header] = columns[col] ?? ''; });
    rows.push({ values, lineNumber: i + 1 });
  }
  return rows;
}

/** Busca un valor de columna sin importar mayúsculas/minúsculas (el backend hace lo mismo). */
function columnValue(row: ParsedCsvRow, column: string): string {
  const key = Object.keys(row.values).find((header) => header.toLowerCase() === column.toLowerCase());
  return key ? row.values[key].trim() : '';
}

const HEADER_SEPARATOR = ' · ';
const UNMARK_WORDS = new Set(['no', 'n', 'false', '0']);

type MarkKind = 'fundamental' | 'behavior' | 'habit';
/** `fundamentalCode` es el Fundamental dueño de la columna (para 'fundamental', su propio código) —
 *  permite inferir el Fundamental de una fila cuando el usuario marcó un Comportamiento/Habit pero se
 *  le olvidó marcar el Fundamental mismo. Mismo campo que el backend agregó a su `MarkColumn`. */
interface MarkColumn { header: string; kind: MarkKind; code: string; fundamentalCode: string; }

function isMarked(raw: string): boolean {
  const trimmed = raw.trim();
  return trimmed.length > 0 && !UNMARK_WORDS.has(trimmed.toLowerCase());
}

function headerCode(rawHeader: string): string {
  const separatorAt = rawHeader.indexOf(HEADER_SEPARATOR);
  return (separatorAt >= 0 ? rawHeader.slice(0, separatorAt) : rawHeader).trim();
}

/** Mismo algoritmo que RecognitionBulkCsvFormat.BuildMarkIndex del backend: una entrada por cada
 *  columna de marca posible, indexada por su código (compuesto para Comportamiento/Habit). Filtra
 *  Comportamientos y Daily Habits por `isActive` (no solo el Fundamental) y descarta un Fundamental
 *  con código vacío — mismo criterio que `BuildMarkIndex`/`fundamentalCodes` del backend, para que una
 *  cabecera vacía (p. ej. por una coma sobrante en la fila de encabezados) no registre una marca. */
function buildMarkIndex(fundamentals: FundamentalDto[]): MarkColumn[] {
  const marks: MarkColumn[] = [];
  for (const fundamental of fundamentals) {
    if (fundamental.code.trim().length === 0) continue;
    marks.push({ header: fundamental.code, kind: 'fundamental', code: fundamental.code, fundamentalCode: fundamental.code });
    for (const behavior of (fundamental.attitudes ?? []).filter((entry) => entry.isActive)) {
      marks.push({ header: `${fundamental.code}.${behavior.code}`, kind: 'behavior', code: behavior.code, fundamentalCode: fundamental.code });
    }
    for (const habit of fundamental.dailyHabits.filter((entry) => entry.isActive)) {
      marks.push({ header: `${fundamental.code}.${habit.code}`, kind: 'habit', code: habit.code, fundamentalCode: fundamental.code });
    }
  }
  return marks;
}

/**
 * Unión de cada Fundamental marcado en la fila, directo o implícito: si el usuario marcó la columna
 * propia del Fundamental, o cualquiera de sus Comportamientos/Daily Habits, ese Fundamental entra al
 * resultado — sin importar cuántos otros Fundamentales también estén marcados en la misma fila.
 *
 * Bug reportado (2026-08-23): con dos Fundamentales en la misma fila, uno marcado directamente y el
 * otro solo a través de su Comportamiento/Habit (el usuario marcó "Aprendiz" y dos hábitos suyos pero
 * no la columna del propio Fundamental), la versión anterior devolvía SOLO el marcado directamente y
 * descartaba el implícito — pero el Comportamiento/Habit marcado seguía viajando en la fila, apuntando
 * a un Fundamental que ya no estaba en la lista: exactamente lo que se veía como "los Fundamentales y
 * Comportamientos/Habits se mezclaron". La versión anterior también rechazaba como "ambiguo" el caso
 * de 2+ Fundamentales implicados sin ninguna marca directa — incoherente con que 1 solo implicado sí
 * se infiriera: si el usuario marcó habits/comportamientos de tres Fundamentales distintos, la lectura
 * más simple es que quiere los tres, no que se rechace la fila entera.
 */
function resolveFundamentalIds(fundamentals: string[], impliedFundamentals: Set<string>): string[] {
  return [...new Set([...fundamentals, ...impliedFundamentals])];
}

/** Resuelve Fundamental/Comportamiento/Habits marcados con X para una fila, contra el catálogo. */
function resolveMarks(
  row: ParsedCsvRow, markIndex: Map<string, MarkColumn>,
): { fundamentalIds: string[]; behaviorIds: string[]; habitIds: string } {
  const fundamentals: string[] = [];
  const behaviors: string[] = [];
  const habits: string[] = [];
  // Fundamentales implicados por los Comportamientos/Habits marcados — para inferir el Fundamental
  // cuando el usuario marcó uno de esos pero se le olvidó marcar el Fundamental mismo.
  const impliedFundamentals = new Set<string>();

  for (const [header, value] of Object.entries(row.values)) {
    const mark = markIndex.get(headerCode(header).toLowerCase());
    if (!mark || !isMarked(value)) continue;
    if (mark.kind === 'fundamental') fundamentals.push(mark.code);
    else if (mark.kind === 'behavior') { behaviors.push(mark.code); impliedFundamentals.add(mark.fundamentalCode); }
    else { habits.push(mark.code); impliedFundamentals.add(mark.fundamentalCode); }
  }

  return {
    fundamentalIds: resolveFundamentalIds(fundamentals, impliedFundamentals),
    // Uno o más Comportamientos por fila — mismo criterio que Fundamentales y Habits, y paridad con el
    // alta individual (ReconocerPanel.tsx), que ya permite varios por reconocimiento.
    behaviorIds: behaviors,
    habitIds: habits.join(';'),
  };
}

/**
 * Mapea filas crudas del CSV al modelo editable del wizard. `CampanaId`/`ExternalId` ya no son
 * columnas de la plantilla: la campaña la fija el contexto del wizard, y `externalId` queda vacío
 * (el backend deriva uno estable a partir del contenido de la fila cuando no viene uno explícito).
 */
export function csvRowsToEditableRows(rows: ParsedCsvRow[], fundamentals: FundamentalDto[]): BulkEditableRow[] {
  const markIndex = new Map(buildMarkIndex(fundamentals).map((mark) => [mark.header.toLowerCase(), mark]));
  return rows.map((row) => {
    const marks = resolveMarks(row, markIndex);
    return {
      lineNumber: row.lineNumber,
      emisorEmail: columnValue(row, 'EmisorEmail'),
      receptorEmail: columnValue(row, 'ReceptorEmail'),
      fundamentalIds: marks.fundamentalIds,
      mensaje: columnValue(row, 'Mensaje'),
      externalId: '',
      receptorNomina: columnValue(row, 'ReceptorNomina'),
      emisorNomina: columnValue(row, 'EmisorNomina'),
      behaviorIds: marks.behaviorIds,
      habitIds: marks.habitIds,
      status: 'checking' as const,
      errorCode: null,
      errorMessage: null,
    };
  });
}
